import React, { useState, useEffect } from "react";
import { Divider, Row, Col, Spin } from "antd";
import { useSelector } from "react-redux";
import PricingItem from "./item";
import { getAllPlans } from "@api/subscription/plan";
import { getInvoices } from "@api/subscription/billing";
import { useTranslation } from "react-i18next";

const Pricing = () => {
  const { user } = useSelector((state) => state.auth);
  const { currentSubscription } = useSelector((state) => state.admin);
  const [plans, setPlans] = useState([]);
  const [billingInterval, setBillingInterval] = useState("year"); // "week" | "month" | "year"
  const [loading, setLoading] = useState(false);
  const [openInvoice, setOpenInvoice] = useState(null);
  const { t } = useTranslation();

  // Set default interval based on user's current subscription
  useEffect(() => {
    if (currentSubscription?.subscription?.billInterval) {
      const interval = currentSubscription.subscription.billInterval;
      if (interval === "week" || interval === "month" || interval === "year") {
        setBillingInterval(interval);
      }
    }
  }, [currentSubscription]);

  // Get all plans
  useEffect(() => {
    setLoading(true);
    getAllPlans().then((res) => {
      // API returns { plans: [...], pagination: {...} }
      setPlans(res.data.plans || res.data || []);
      setLoading(false);
    }).catch((err) => {
      console.error("Failed to load plans:", err);
      setLoading(false);
    });
  }, []);

  // Check for open invoices that need payment
  useEffect(() => {
    getInvoices({ status: "open", limit: 1 }).then((res) => {
      const invoices = res.data?.invoices || res.data || [];
      if (invoices.length > 0) {
        setOpenInvoice(invoices[0]);
      }
    }).catch((err) => {
      console.error("Failed to check invoices:", err);
    });
  }, [currentSubscription]);

  // Interval toggle button style helper
  const getIntervalButtonStyle = (interval) => {
    const isActive = billingInterval === interval;
    return {
      padding: "8px 20px",
      background: isActive ? "#fff" : "transparent",
      border: "none",
      borderRadius: "8px",
      fontSize: "13px",
      fontWeight: 500,
      color: isActive ? "#1e293b" : "#64748b",
      cursor: "pointer",
      transition: "all 0.15s ease",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      boxShadow: isActive ? "0 1px 3px rgba(0, 0, 0, 0.1)" : "none",
    };
  };

  // Get billed text based on interval
  const getBilledText = () => {
    switch (billingInterval) {
      case "week":
        return t("profile.subscription.billedWeekly") || "Billed weekly";
      case "month":
        return t("profile.subscription.billedMonthly") || "Billed monthly";
      case "year":
        return t("profile.subscription.billedAnnually") || "Billed annually";
      default:
        return "";
    }
  };

  return loading ? (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        height: "calc(80vh - 50px)",
      }}
    >
      <Spin />
    </div>
  ) : (
    <Row gutter={[32, 32]} className="hp-mb-32">
      <Col span={24}>
        <Row>
          {/* Header */}
          <Col span={24}>
            <h2>{t("profile.subscription.subscriptionDetails")}</h2>
            <p className="hp-p1-body hp-mb-0">
              {t("profile.subscription.subscriptionDetailsDesc")}
            </p>
          </Col>

          {/* Payment Required Banner */}
          {openInvoice && (
            <Col span={24} style={{ marginTop: "16px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  padding: "16px 20px",
                  background: "linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)",
                  border: "1px solid #fbbf24",
                  borderRadius: "12px",
                  gap: "16px",
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                  <div
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      background: "rgba(251, 191, 36, 0.3)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      color: "#b45309",
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                  </div>
                  <div>
                    <div style={{ fontWeight: 600, color: "#92400e", fontSize: "15px" }}>
                      {t("profile.subscription.paymentRequired") || "Payment Required"}
                    </div>
                    <div style={{ color: "#a16207", fontSize: "13px" }}>
                      {t("profile.subscription.completePaymentToActivate") || "Complete payment on the open invoice below to activate your plan change."}
                    </div>
                  </div>
                </div>
                <button
                  onClick={() => {
                    if (openInvoice?.hostedInvoiceUrl || openInvoice?.hostedUrl) {
                      window.open(openInvoice.hostedInvoiceUrl || openInvoice.hostedUrl, "_blank");
                    }
                  }}
                  style={{
                    padding: "10px 20px",
                    background: "linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%)",
                    color: "#000",
                    border: "none",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: "14px",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    transition: "all 0.2s ease",
                    boxShadow: "0 2px 8px rgba(251, 191, 36, 0.3)",
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.transform = "translateY(-1px)";
                    e.target.style.boxShadow = "0 4px 12px rgba(251, 191, 36, 0.4)";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.transform = "translateY(0)";
                    e.target.style.boxShadow = "0 2px 8px rgba(251, 191, 36, 0.3)";
                  }}
                >
                  {t("profile.subscription.payNow") || "Pay Now"}
                </button>
              </div>
            </Col>
          )}

          <Divider className="hp-border-color-black-40 hp-border-color-dark-80" />

          {/* Plans */}
          <Col span={24}>
            {!user?.appSumoUser && (
              <>
                {/* Three-way interval toggle */}
                <Row
                  align="middle"
                  justify="center"
                  className="hp-mt-32 hp-mb-24"
                >
                  <div
                    style={{
                      display: "inline-flex",
                      padding: "4px",
                      background: "#f1f5f9",
                      borderRadius: "10px",
                    }}
                  >
                    {/* Weekly Button */}
                    <button
                      onClick={() => setBillingInterval("week")}
                      style={getIntervalButtonStyle("week")}
                    >
                      {t("profile.subscription.weekly") || "Weekly"}
                    </button>

                    {/* Monthly Button */}
                    <button
                      onClick={() => setBillingInterval("month")}
                      style={getIntervalButtonStyle("month")}
                    >
                      {t("profile.subscription.monthly") || "Monthly"}
                    </button>

                    {/* Annual Button */}
                    <button
                      onClick={() => setBillingInterval("year")}
                      style={getIntervalButtonStyle("year")}
                    >
                      {t("profile.subscription.annual") || "Annual"}
                      <span
                        style={{
                          padding: "2px 8px",
                          background: "#dcfce7",
                          color: "#059669",
                          borderRadius: "10px",
                          fontSize: "10px",
                          fontWeight: 600,
                        }}
                      >
                        {t("profile.subscription.save20") || "Save 20%"}
                      </span>
                    </button>
                  </div>
                </Row>

                {/* Save message for annual */}
                {billingInterval === "year" && (
                  <Row
                    align="middle"
                    justify="center"
                    className="hp-mb-24"
                  >
                    <h5 className="hp-text-color-primary-1" style={{ margin: 0 }}>
                      {t("profile.subscription.savePlan") || "Save 2 months on annual plan!"}
                    </h5>
                  </Row>
                )}
              </>
            )}

            {/* Pricing Item Component */}
            <PricingItem
              user={user}
              plans={[...plans].sort((a, b) => {
                // Free plan always first
                const aIsFree = a.name?.toLowerCase() === "free" || (a.monthlyPrice === 0 && a.annualPrice === 0);
                const bIsFree = b.name?.toLowerCase() === "free" || (b.monthlyPrice === 0 && b.annualPrice === 0);
                if (aIsFree && !bIsFree) return -1;
                if (!aIsFree && bIsFree) return 1;
                // Then by tier/price
                const tierMap = { Free: 0, Optimizer: 1, Wizard: 2, Maverick: 3 };
                const aTier = tierMap[a.name] ?? (a.monthlyPrice || 0);
                const bTier = tierMap[b.name] ?? (b.monthlyPrice || 0);
                return aTier - bTier;
              })}
              annualCycle={billingInterval === "year"}
              billingInterval={billingInterval}
              billedText={getBilledText()}
              currentSubscription={currentSubscription}
              billingAdmin={currentSubscription?.billingAdmin}
            />
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default Pricing;
